import React, { useState, useEffect } from 'react';
import EditModal from '../../components/Modals/EditModal';
import Layout from '../../layout/Layout';
import fetchData from '../../functions/fetchData';
import updateData from '../../functions/updateData';
import  ExportButtons  from '../../components/ExportButtons';
import './FoodManagement.css';

const FoodManagement = () => {
  const [activeTab, setActiveTab] = useState('category');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Category Management State
  // Edit Modal State
  const [editModal, setEditModal] = useState({ show: false, record: null, type: '' });
  const [categories, setCategories] = useState([]);
  const [categoryForm, setCategoryForm] = useState({ name: '' });

  // Item Management State
  const [items, setItems] = useState([]);
  const [itemForm, setItemForm] = useState({
    brand: '',
    name: '',
    code: '',
    uuid: 0
  });

  // Pricing Management State
  const [prices, setPrices] = useState([]);
  const [pricingForm, setPricingForm] = useState({
    date: new Date().toISOString().split('T')[0],
    brand: '',
    name: '',
    price: '',
    category: 'Regular',
    zone: 'club',
    code: '',
    uuid: 0
  });

  // Search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Food categories for dropdown
  

  useEffect(() => {
    fetchCategories();
    fetchItems();
    fetchPrices();
  }, []);

  // Fetch Categories (using food categories)
  const fetchCategories = async () => {
    try {
      // Since food categories are predefined, we'll use static data
       await fetchData('food_category', setCategories, 'id', { flag: 1 });
      
     
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch Items
  const fetchItems = async () => {
    try {
     await fetchData('food_items', setItems, 'id', { flag: 1 });
      
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  // Fetch Prices
  const fetchPrices = async () => {
    try {
      await fetchData('food_rate', setPrices, 'id', { flag: 1 });
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };

  // Handle Category Submit (for food, categories are predefined but we can add custom ones)
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      // Add to local state for custom categories
      const newCategory = {
        id: categories.length + 1,
        name: categoryForm.name
      };
      setCategories([...categories, newCategory]);
      setSuccess('Category added successfully!');
      setCategoryForm({ name: '' });
    } catch (error) {
      console.error('Error adding category:', error);
      setError('Error adding category');
    } finally {
      setLoading(false);
    }
  };

  // Handle Item Submit
  // Handle Edit Save (top-level, accessible to modal)
  const handleEditSave = (updatedRecord) => {
    setEditModal({ show: false, record: null, type: '' });
    setLoading(true);
    setError('');
    let table = '';
    if (editModal.type === 'item') table = 'food_items';
    if (editModal.type === 'pricing') table = 'food_rate';
    const where = { id: updatedRecord.id };
    updateData(table, updatedRecord, where, 'PUT')
      .then(res => {
        if (res && res.success) {
          setSuccess('Record updated successfully!');
          if (editModal.type === 'item') fetchItems();
          if (editModal.type === 'pricing') fetchPrices();
        } else {
          setError('Error updating record');
        }
      })
      .catch(() => setError('Error updating record'))
      .finally(() => setLoading(false));
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      const response = await updateData('food_items', itemForm, {}, 'POST');
      if (response && response.success) {
        setSuccess('Item added successfully!');
        setItemForm({ brand: '', name: '', code: '', uuid: 0 });
        fetchItems();
      } else {
        setError('Error adding item');
      }
    } catch (error) {
      console.error('Error adding item:', error);
      setError('Error adding item');
    } finally {
      setLoading(false);
    }
  };

  // Handle Pricing Submit
  const handlePricingSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      const response = await updateData('food_rate', pricingForm, {}, 'POST');
      if (response && response.success) {
        setSuccess('Pricing added successfully!');
        setPricingForm({
          date: new Date().toISOString().split('T')[0],
          brand: '',
          name: '',
          price: '',
          category: 'Regular',
          zone: 'club',
          code: '',
          uuid: 0
        });
        fetchPrices();
      } else {
        setError('Error adding pricing');
      }
    } catch (error) {
      console.error('Error adding pricing:', error);
      setError('Error adding pricing');
    } finally {
      setLoading(false);
    }
  };

  // Handle Status Toggle (Activate/Deactivate) for Food
  const handleStatusToggle = async (price) => {
    try {
      setLoading(true);
      setError('');
      
      // If category is empty/null, set to Regular (activate), otherwise set to empty (deactivate)
      const newCategory = (!price.category || price.category === '') ? 'Regular' : '';
      const statusText = newCategory === 'Regular' ? 'activated' : 'deactivated';
      
      const whereCondition = { id: price.id };
      const updateData_obj = { category: newCategory };
      
      const response = await updateData('food_rate', updateData_obj, whereCondition, 'PUT');
      
      if (response && response.success) {
        setSuccess(`Food item ${statusText} successfully!`);
        fetchPrices();
      } else {
        setError('Error updating status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Error updating status');
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const getFilteredData = (data, searchFields) => {
    if (!searchTerm) return data;
    return data.filter(item =>
      searchFields.some(field =>
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  // Pagination
  const paginate = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  // Generate pagination numbers with ellipsis for better UX
  const generatePaginationItems = (totalPages) => {
    const items = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        items.push(i);
      }
    } else {
      // Show first page
      items.push(1);
      
      let startPage, endPage;
      
      if (currentPage <= 3) {
        // If current page is near the beginning
        startPage = 2;
        endPage = 4;
      } else if (currentPage >= totalPages - 2) {
        // If current page is near the end
        startPage = totalPages - 3;
        endPage = totalPages - 1;
      } else {
        // If current page is in the middle
        startPage = currentPage - 1;
        endPage = currentPage + 1;
      }
      
      // Add ellipsis if needed
      if (startPage > 2) {
        items.push('...');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        items.push(i);
      }
      
      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        items.push('...');
      }
      
      // Show last page
      if (totalPages > 1) {
        items.push(totalPages);
      }
    }
    
    return items;
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(clearMessages, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const renderCategoryTab = () => {
    const filteredCategories = getFilteredData(categories, ['name']);
    const paginatedCategories = paginate(filteredCategories);
    const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

    return (
      <div className="tab-content">
        <div className="row">
          <div className="col-md-4">
            <div className="form-section">
              <h5><i className="zmdi zmdi-plus-circle mr-2"></i> Add New Category</h5>
              <form onSubmit={handleCategorySubmit}>
                <div className="form-group">
                  <label className="control-label">Category Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ name: e.target.value })}
                    placeholder="Enter category name"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-submit" disabled={loading}>
                  {loading && <span className="loading-spinner"></span>}
                  Add Category
                </button>
              </form>
            </div>
          </div>

          <div className="col-md-8">
            <div className="table-section">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6><i className="zmdi zmdi-view-list mr-2"></i> Food Categories ({filteredCategories.length})</h6>
              
              </div>

              

              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <td colSpan={2}>
                        <div className="search-box">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </td>
                      <td colSpan={1}>
                        <ExportButtons
                          data={filteredCategories}
                          filename="food-categories"
                          title="Food Categories Report"
                          columns={[
                            { key: 'id', label: 'ID' },
                            { key: 'name', label: 'Category Name' }
                          ]}
                          size="sm"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>ID</th>
                      <th>Category Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCategories.map((category) => (
                      <tr key={category.id}>
                        <td>{category.id}</td>
                        <td><strong>{category.name}</strong></td>
                        <td className="action-buttons">
                          <button className="btn btn-info btn-xs" title="Edit">
                            <i className="zmdi zmdi-edit"></i>
                          </button>
                          <button className="btn btn-danger btn-xs ml-1" title="Delete">
                            <i className="zmdi zmdi-delete"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination-wrapper mt-3">
                  <nav>
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                          Previous
                        </button>
                      </li>
                      {generatePaginationItems(totalPages).map((item, index) => (
                        <li key={index} className={`page-item ${item === currentPage ? 'active' : ''} ${item === '...' ? 'disabled' : ''}`}>
                          {item === '...' ? (
                            <span className="page-link">...</span>
                          ) : (
                            <button className="page-link" onClick={() => setCurrentPage(item)}>
                              {item}
                            </button>
                          )}
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderItemTab = () => {
    const filteredItems = getFilteredData(items, ['brand', 'name', 'code']);
    const paginatedItems = paginate(filteredItems);
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

    return (
      <div className="tab-content">
        <div className="row">
          <div className="col-md-4">
            <div className="form-section">
              <h5><i className="zmdi zmdi-plus-circle mr-2"></i> Add New Food Item</h5>
              <form onSubmit={handleItemSubmit}>
                <div className="form-group">
                  <label className="control-label">Category *</label>
                  <select
                    className="form-control"
                    value={itemForm.brand}
                    onChange={(e) => setItemForm({ ...itemForm, brand: e.target.value })}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="control-label">Item Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={itemForm.name}
                    onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                    placeholder="Enter item name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="control-label">Item Code *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={itemForm.code}
                    onChange={(e) => setItemForm({ ...itemForm, code: e.target.value })}
                    placeholder="Enter item code"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-submit" disabled={loading}>
                  {loading && <span className="loading-spinner"></span>}
                  Add Food Item
                </button>
              </form>
            </div>
          </div>

          <div className="col-md-8">
            <div className="table-section">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6><i className="zmdi zmdi-view-list mr-2"></i> Food Items ({filteredItems.length})</h6>
               
              </div>

              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <td colSpan={3}>
                        <div className="search-box">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search items..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </td>
                      <td colSpan={2}>
                        <ExportButtons
                          data={filteredItems}
                          filename="food-items"
                          title="Food Items Report"
                          columns={[
                            { key: 'id', label: 'ID' },
                            { key: 'brand', label: 'Category' },
                            { key: 'name', label: 'Item Name' },
                            { key: 'code', label: 'Code' }
                          ]}
                          size="sm"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>ID</th>
                      <th>Category</th>
                      <th>Item Name</th>
                      <th>Code</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedItems.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td><span className="badge badge-warning">{item.brand}</span></td>
                        <td><strong>{item.name}</strong></td>
                        <td>{item.code}</td>
                        <td className="action-buttons">
                          <button className="btn btn-info btn-xs" title="Edit" onClick={() => setEditModal({ show: true, record: item, type: 'item' })}>
                            <i className="zmdi zmdi-edit"></i>
                          </button>
                          <button className="btn btn-danger btn-xs ml-1" title="Delete">
                            <i className="zmdi zmdi-delete"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination-wrapper mt-3">
                  <nav>
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                          Previous
                        </button>
                      </li>
                      {generatePaginationItems(totalPages).map((item, index) => (
                        <li key={index} className={`page-item ${item === currentPage ? 'active' : ''} ${item === '...' ? 'disabled' : ''}`}>
                          {item === '...' ? (
                            <span className="page-link">...</span>
                          ) : (
                            <button className="page-link" onClick={() => setCurrentPage(item)}>
                              {item}
                            </button>
                          )}
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPricingTab = () => {
    const filteredPrices = getFilteredData(prices, ['brand', 'name', 'price', 'category', 'zone', 'code']);
    const paginatedPrices = paginate(filteredPrices);
    const totalPages = Math.ceil(filteredPrices.length / itemsPerPage);

    return (
      <div className="tab-content">
        <div className="row">
          <div className="col-md-4">
            <div className="form-section">
              <h5><i className="zmdi zmdi-plus-circle mr-2"></i> Add Food Pricing</h5>
              <form onSubmit={handlePricingSubmit}>
                <div className="form-group">
                  <label className="control-label">Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={pricingForm.date}
                    onChange={(e) => setPricingForm({ ...pricingForm, date: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="control-label">Category *</label>
                  <select
                    className="form-control"
                    value={pricingForm.brand}
                    onChange={(e) => setPricingForm({ ...pricingForm, brand: e.target.value })}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="control-label">Food Item *</label>
                  <select
                    className="form-control"
                    value={pricingForm.name}
                    onChange={(e) => setPricingForm({ ...pricingForm, name: e.target.value })}
                    required
                  >
                    <option value="">Select Food Item</option>
                    {items
                      .filter(item => item.brand === pricingForm.brand)
                      .map((item) => (
                        <option key={item.id} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="control-label">Price () *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={pricingForm.price}
                    onChange={(e) => setPricingForm({ ...pricingForm, price: e.target.value })}
                    placeholder="Enter price"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="control-label">Price Category</label>
                  <select
                    className="form-control"
                    value={pricingForm.category}
                    onChange={(e) => setPricingForm({ ...pricingForm, category: e.target.value })}
                  >
                    <option value="Regular">Regular</option>
                    <option value="Premium">Premium</option>
                    <option value="VIP">VIP</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="control-label">Zone</label>
                  <select
                    className="form-control"
                    value={pricingForm.zone}
                    onChange={(e) => setPricingForm({ ...pricingForm, zone: e.target.value })}
                  >
                    <option value="club">Club</option>
                    <option value="pool">Pool</option>
                    <option value="skybar">Skybar</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="control-label">Code</label>
                  <input
                    type="number"
                    className="form-control"
                    value={pricingForm.code}
                    onChange={(e) => setPricingForm({ ...pricingForm, code: e.target.value })}
                    placeholder="Enter code"
                  />
                </div>
                <button type="submit" className="btn btn-submit" disabled={loading}>
                  {loading && <span className="loading-spinner"></span>}
                  Add Pricing
                </button>
              </form>
            </div>
          </div>

          <div className="col-md-8">
            <div className="table-section">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6><i className="zmdi zmdi-view-list mr-2"></i> Food Pricing ({filteredPrices.length})</h6>
                
              </div>

              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <td colSpan={4}>
                        <div className="search-box">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search pricing..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </td>
                      <td colSpan={4}>
                        <ExportButtons
                          data={filteredPrices}
                          filename="food-pricing"
                          title="Food Pricing Report"
                          columns={[
                            { key: 'id', label: 'ID' },
                            { key: 'brand', label: 'Category' },
                            { key: 'name', label: 'Food Item' },
                            { key: 'price', label: 'Price', formatter: (value) => `${value}` },
                            { key: 'category', label: 'Price Category', formatter: (value) => value && value !== '' ? value : 'Inactive' },
                            { key: 'zone', label: 'Zone' }
                          ]}
                          size="sm"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>ID</th>
                      <th>Category</th>
                      <th>Food Item</th>
                      <th>Price</th>
                      <th>Price Category</th>
                      <th>Zone</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPrices.map((price) => (
                      <tr key={price.id}>
                        <td>{price.id}</td>
                        <td>{price.brand}</td>
                        <td>{price.name}</td>
                        <td>{price.price}</td>
                        <td>{price.category && price.category !== '' ? price.category : 'Inactive'}</td>
                        <td>{price.zone}</td>
                        <td>
                          {price.category && price.category !== '' ? (
                            <button 
                              className="btn btn-warning btn-xs" 
                              title="Deactivate"
                              onClick={() => handleStatusToggle(price)}
                              disabled={loading}
                            >
                              <i className="zmdi zmdi-pause"></i> Deactivate
                            </button>
                          ) : (
                            <button 
                              className="btn btn-success btn-xs" 
                              title="Activate"
                              onClick={() => handleStatusToggle(price)}
                              disabled={loading}
                            >
                              <i className="zmdi zmdi-play"></i> Activate
                            </button>
                          )}
                        </td>
                        <td className="action-buttons d-flex flex-wrap justify-content-center" style={{gap: '4px', minWidth: '90px'}}>
                          <button className="btn btn-info btn-xs" title="Edit" onClick={() => setEditModal({ show: true, record: price, type: 'pricing' })}>
                            <i className="zmdi zmdi-edit"></i>
                          </button>
                          <button className="btn btn-danger btn-xs ml-1" title="Delete">
                            <i className="zmdi zmdi-delete"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination-wrapper mt-3">
                  <nav>
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                          Previous
                        </button>
                      </li>
                      {generatePaginationItems(totalPages).map((item, index) => (
                        <li key={index} className={`page-item ${item === currentPage ? 'active' : ''} ${item === '...' ? 'disabled' : ''}`}>
                          {item === '...' ? (
                            <span className="page-link">...</span>
                          ) : (
                            <button className="page-link" onClick={() => setCurrentPage(item)}>
                              {item}
                            </button>
                          )}
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="food-management-container">
        <div className="content-header">
          <h2><i className="zmdi zmdi-pizza mr-2"></i>Food Management</h2>
          <p className="content-description">Manage food categories, items, and pricing</p>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="zmdi zmdi-alert-circle mr-2"></i>
            {error}
            <button type="button" className="close" onClick={clearMessages}>
              <span>&times;</span>
            </button>
          </div>
        )}

        {success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="zmdi zmdi-check-circle mr-2"></i>
            {success}
            <button type="button" className="close" onClick={clearMessages}>
              <span>&times;</span>
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'category' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('category');
                  setCurrentPage(1);
                  setSearchTerm('');
                }}
              >
                <i className="zmdi zmdi-label mr-2"></i>Categories
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'items' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('items');
                  setCurrentPage(1);
                  setSearchTerm('');
                }}
              >
                <i className="zmdi zmdi-pizza mr-2"></i>Food Items
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'pricing' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('pricing');
                  setCurrentPage(1);
                  setSearchTerm('');
                }}
              >
                <i className="zmdi zmdi-money mr-2"></i>Pricing
              </button>
            </li>
          </ul>
        </div>

        {/* Tab Content */}
        <div className="tab-container">
          {activeTab === 'category' && renderCategoryTab()}
          {activeTab === 'items' && renderItemTab()}
          {activeTab === 'pricing' && renderPricingTab()}
        </div>
        {/* Edit Modal */}
        {editModal.show && (
          <EditModal
            record={editModal.record}
            onClose={() => setEditModal({ show: false, record: null, type: '' })}
            onSave={handleEditSave}
          />
        )}
      </div>
    </Layout>
  );
};

export default FoodManagement;
